import { useState } from 'react';
import { X, Mail, Lock, Loader2 } from 'lucide-react';

const Login = ({ onClose, onLoginSuccess, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // 1. Appel à /login avec du JSON
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          username: "" // Pour compatibilité avec ton UserCreate
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Identifiants incorrects');
      }

      // 2. Récupération du profil complet (pseudo)
      const userRes = await fetch('/api/users/me', {
        headers: { 'Authorization': `Bearer ${data.access_token}` }
      });
      
      if (!userRes.ok) throw new Error("Impossible de récupérer le profil");
      
      const userData = await userRes.json();

      // 3. Succès
      onLoginSuccess(userData, data.access_token);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
        
        {/* Header de la Modal */}
        <div className="px-8 pt-8 pb-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Welcome Back!</h2>
            <p className="text-slate-400 text-sm mt-1">Log in to Scribify</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-8 pt-4 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm animate-in fade-in zoom-in-95">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300 ml-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="email"
                required
                className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-emerald-500 outline-none transition-all"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300 ml-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="password"
                required
                className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-emerald-500 outline-none transition-all"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-bold rounded-xl transition-all flex items-center justify-center space-x-2 active:scale-[0.98] shadow-lg shadow-emerald-900/20"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <span>Log In</span>
            )}
          </button>
        </form>

        {/* Footer de la Modal : Lien de bascule vers Register */}
        <div className="px-8 pb-8 text-center border-t border-slate-800 pt-6">
          <p className="text-slate-500 text-sm">
            Don't have an account ?{' '}
            <button 
              type="button" 
              onClick={onSwitchToRegister}
              className="text-emerald-500 hover:text-emerald-400 font-semibold transition-colors underline-offset-4 hover:underline"
            >
              Sign up here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;